// components/NewOrderSelector/components/Modifiers/Modifiers.tsx

import React, { useState, useMemo } from 'react';
import { Modifier } from '../../../../types';
import './Modifiers.scss'; // Import the SCSS file
import { formatCurrency } from '../../../../../../../../../../../utils/functions';
import { FaChevronDown, FaChevronUp, FaSearch } from 'react-icons/fa'; // Using react-icons for chevron

interface ModifiersProps {
 modifiers: Modifier[];
 selectedModifiers: { [modifierId: number]: number };
 handleModifierOptionChange: (modifier: Modifier, optionId: number) => void;
}

const Modifiers: React.FC<ModifiersProps> = ({ modifiers, selectedModifiers, handleModifierOptionChange }) => {
 // State to manage collapsed/expanded for each modifier
 const [collapsedModifiers, setCollapsedModifiers] = useState<{ [modifierId: number]: boolean }>({});
 
 // State to manage search terms for each modifier
 const [searchTerms, setSearchTerms] = useState<{ [modifierId: number]: string }>({});

 // Sort modifiers: mandatory first (unfolded), optional after (folded by default)
 const sortedModifiers = useMemo(() => {
   return [...modifiers].sort((a, b) => {
     const aMandatory = !a.is_optional;
     const bMandatory = !b.is_optional;
     
     if (aMandatory && !bMandatory) return -1;
     if (!aMandatory && bMandatory) return 1;
     return 0;
   });
 }, [modifiers]);

 // Initialize collapsed state based on whether modifier is mandatory
 React.useEffect(() => {
   const initialCollapsedState: { [modifierId: number]: boolean } = {};
   sortedModifiers.forEach(modifier => {
     const isMandatory = !modifier.is_optional;
     initialCollapsedState[modifier.modifier_id] = !isMandatory; // Mandatory: false (unfolded), Optional: true (folded)
   });
   setCollapsedModifiers(initialCollapsedState);
 }, [sortedModifiers]);

 // Toggle collapsed state
 const toggleCollapse = (modifierId: number) => {
   setCollapsedModifiers(prev => ({
     ...prev,
     [modifierId]: !prev[modifierId],
   }));
 };

 // Handle search term change
 const handleSearchChange = (modifierId: number, term: string) => {
   setSearchTerms(prev => ({
     ...prev,
     [modifierId]: term,
   }));
 };

 // Filter options based on search term
 const getFilteredOptions = (modifier: Modifier) => {
   const searchTerm = searchTerms[modifier.modifier_id] || '';
   if (!searchTerm) return modifier.options;
   
   return modifier.options.filter(option =>
     option.option_name.toLowerCase().includes(searchTerm.toLowerCase())
   );
 };

 return (
   <div className="modifiers-container">
     {sortedModifiers.map(modifier => {
       // Defensive validation
       if (!modifier || !modifier.modifier_id) {
         console.error('Invalid modifier:', modifier);
         return null;
       }
       
       if (!Array.isArray(modifier.options)) {
         console.error('Modifier missing options array:', modifier);
         return null;
       }
       
       const isMandatory = !modifier.is_optional;
       const hasSearchBar = modifier.options.length > 2; // Set to > 2 for testing, change to > 12 later
       const filteredOptions = getFilteredOptions(modifier);

       return (
         <div key={modifier.modifier_id} className={`modifier-item ${isMandatory ? 'mandatory' : 'optional'}`}>
           <div className="modifier-header" onClick={() => toggleCollapse(modifier.modifier_id)}>
             <div className="header-content">
               <h3 className="modifier-name">
                 {modifier.modifier_name}
                 {isMandatory && <span className="required-indicator">*</span>}
               </h3>
               <span className="modifier-info">
                 {isMandatory ? '(Requerido)' : '(Opcional)'}
               </span>
             </div>
             <span className={`chevron-icon ${collapsedModifiers[modifier.modifier_id] ? 'collapsed' : 'expanded'}`}>
               {collapsedModifiers[modifier.modifier_id] ? <FaChevronDown /> : <FaChevronUp />}
             </span>
           </div>
           
           {!collapsedModifiers[modifier.modifier_id] && (
             <div className="modifier-content">
               <p className="modifier-description">{modifier.modifier_description}</p>
               
               {hasSearchBar && (
                 <div className="search-container">
                   <div className="search-input-wrapper">
                     <FaSearch className="search-icon" />
                     <input
                       type="text"
                       placeholder={`Buscar en ${modifier.modifier_name}...`}
                       value={searchTerms[modifier.modifier_id] || ''}
                       onChange={(e) => handleSearchChange(modifier.modifier_id, e.target.value)}
                       className="search-input"
                     />
                   </div>
                 </div>
               )}
               
               <div className="modifier-options">
                 {modifier.is_optional && (
                   <div className={`modifier-option ${selectedModifiers[modifier.modifier_id] === 0 ? 'selected' : ''}`}>
                     <label className="modifier-label">
                       <input
                         type="radio"
                         name={`modifier_${modifier.modifier_id}`}
                         value="0"
                         checked={selectedModifiers[modifier.modifier_id] === 0}
                         onChange={() => handleModifierOptionChange(modifier, 0)}
                         className="modifier-input"
                       />
                       <span className="modifier-text">No agregar</span>
                     </label>
                   </div>
                 )}
                 
                 {filteredOptions.map(option => (
                   <div key={option.option_id} className={`modifier-option ${selectedModifiers[modifier.modifier_id] === option.option_id ? 'selected' : ''}`}>
                     <label className="modifier-label">
                       <input
                         type="radio"
                         name={`modifier_${modifier.modifier_id}`}
                         value={option.option_id}
                         checked={selectedModifiers[modifier.modifier_id] === option.option_id}
                         onChange={() => handleModifierOptionChange(modifier, option.option_id)}
                         className="modifier-input"
                       />
                       <span className="modifier-text">{option.option_name}</span>
                       {option.price_modifier !== 0 && (
                         <span className="price-modifier">
                           +{formatCurrency(option.price_modifier)}
                         </span>
                       )}
                     </label>
                   </div>
                 ))}
                 
                 {filteredOptions.length === 0 && searchTerms[modifier.modifier_id] && (
                   <div className="no-results">
                     No se encontraron opciones que coincidan con "{searchTerms[modifier.modifier_id]}"
                   </div>
                 )}
               </div>
             </div>
           )}
         </div>
       );
     })}
   </div>
 );
};

export default Modifiers;
